import React, {useContext, useEffect, useState} from "react";
import {Alert, Button, Card, Radio, Space} from "antd";
import '../assets/css/ChoosePlan.css'
import {FormattedMessage, useIntl} from "react-intl";
import {isMobileOnly} from "react-device-detect";
import {
    Organization,
    PlanResponse, MonetaryType,
    SubscriptionStatus
} from "../api-client";
import {DentobookApplicationContext} from "../context/DentobookApplicationContext";
import PaymentDetails from "./PaymentDetails";
import {CloseOutlined} from "@ant-design/icons";
import {AlertContext} from "../context/AlertContext";
import type {StripeElementsOptions, Stripe} from "@stripe/stripe-js";
import {Navigate} from "react-router-dom";
import dayjs from "dayjs";
import {getFeaturesApi, getOrganizationApi, getStripeApi} from "../api/apiInstances";
import {SubscriptionState} from "../constants/subscriptionStates";
import {ALERT_DURATIONS} from "../constants/alerts";
import logger from "../utils/logger";
import { useTrackEvent } from "../hooks/useTrackEvent";

// TODO: FEATURE FLAG - This component has Stripe payment feature flag logic
// When re-enabling payments, search for "TODO: FEATURE FLAG" and remove those sections
// Key areas: loadData(), handleContinueBtnClick(), handleClose(), cardFormVisible check
const ChoosePlan: React.FC = () => {

    const intl = useIntl();
    const {selectSubscription, setSelectSubscription, principal, setPrincipal} = useContext(DentobookApplicationContext);
    const {setSuccessMessage} = useContext(AlertContext);
    const [organization, setOrganization] = useState<Organization | undefined>(undefined);
    const [cardFormVisible, setCardFormVisible] = useState<boolean>(false);
    const [redirectToHome, setRedirectToHome] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [selectedPlan, setSelectedPlan] = useState<string>('');
    const [plans, setPlans] = useState<PlanResponse[]>([]);

    const organizationApi = getOrganizationApi();
    const featureApi = getFeaturesApi();
    const stripeApi = getStripeApi();

    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | undefined>(undefined);
    const [StripeElements, setStripeElements] = useState<any>(null);
    const track = useTrackEvent();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        organizationApi.fetchOrganizationById(principal?.user?.organizationId as string)
            .then((response) => {
                // @ts-ignore
                setOrganization(response.data);
            })
            .catch(error => {
                logger.error("Failed to load organization:", error);
                logger.log(error);
            });
        const plansResponse = await organizationApi.fetchAllPlans();
        setPlans(plansResponse.data);

        // Check if Stripe is enabled globally (STRIPE_ENABLED environment variable)
        let stripeEnabled = false;
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/enabled`);
            const data = await response.json();
            stripeEnabled = data?.enabled ?? false;
        } catch (error) {
            logger.error('Error checking Stripe enabled status:', error);
        }

        if (!stripeEnabled) {
            // Stripe disabled globally (STRIPE_ENABLED=false) - no Stripe API call
            logger.log('Stripe disabled (STRIPE_ENABLED=false) - payment processing unavailable');
        } else {
            // Stripe enabled - load configuration
            try {
                const response = await stripeApi.getPublishableKey();
                const {publishableKey} = response.data!;

                // Only initialize Stripe if we have a valid key
                if (publishableKey && publishableKey.trim() !== '') {
                    // Lazy load Stripe library only when needed
                    Promise.all([
                        import('@stripe/stripe-js'),
                        import('@stripe/react-stripe-js')
                    ]).then(([stripeJs, reactStripeJs]) => {
                        setStripePromise(stripeJs.loadStripe(publishableKey));
                        setStripeElements(() => reactStripeJs.Elements);
                    });
                    if (selectSubscription === SubscriptionState.SHOW_PAID_PLAN_SELECTED) {
                        setSelectedPlan(plansResponse.data.find((plan) => plan.monetaryType === 'PAID')?.id ?? '');
                        setCardFormVisible(true);
                    }
                } else {
                    // Stripe key not configured
                    logger.log('Stripe publishable key not configured');
                }
            } catch (error) {
                logger.error("Failed to load Stripe configuration:", error);
            }
        }
    };

    const handleContinueBtnClick = async (e: any) => {
        e.preventDefault();

        if (!selectedPlan) {
            const subscriptionExpiration = organization?.subscription?.expiration
                ? dayjs(organization.subscription.expiration)
                : null;
            if (organization?.subscription?.status !== SubscriptionStatus.Active || !organization?.subscription || subscriptionExpiration?.isBefore(dayjs()) ) {
                setShowError(true);
                return;
            }
            setRedirectToHome(true);
        }
        const selectedPlanDetails = plans.find(plan => plan.id === selectedPlan);

        if (selectedPlanDetails?.monetaryType === MonetaryType.FreeTrial) {
            track('plan_selected', { plan_type: 'free_trial', stripe_shown: false });
            await handleSubscriptionCreate(selectedPlan);
            setSelectSubscription(SubscriptionState.HIDDEN);
        } else if (selectedPlanDetails?.monetaryType === MonetaryType.Paid) {
            track('plan_selected', { plan_type: 'paid', stripe_shown: !!stripePromise });
            // TODO: FEATURE FLAG - Only show payment form if Stripe is enabled
            if (stripePromise) {
                setCardFormVisible(true);
            } else {
                // Payments disabled - give them paid plan with all features for free
                logger.log('Payment required but Stripe disabled - giving paid plan with all features');
                const paidPlan = plans.find((plan) => plan.monetaryType === MonetaryType.Paid);
                if (paidPlan?.id) {
                    await handleSubscriptionCreate(paidPlan.id);
                }
                setRedirectToHome(true);
            }
            // END TODO: FEATURE FLAG
        } else {
            setCardFormVisible(true);
        }
    };

    const handleAlertClick = () => setShowError(false);

    const handleSubscriptionCreate = async (planId?: string) => {
        // All organizations have subscriptions - just fetch features
        const featuresResponse = await featureApi.fetchFeaturesByOrganization(principal?.user?.organizationId!);
        const featuresArray = featuresResponse.data.map(feature => feature.name!);
        setPrincipal({...principal!, features: featuresArray});

        setSelectSubscription(SubscriptionState.HIDDEN);
        setSuccessMessage(intl.formatMessage({id: 'registration_completed'}));
        setTimeout(() => setSuccessMessage(''), ALERT_DURATIONS.STANDARD);
    };


    const handleClose = async () => {
        const subscriptionExpiration = organization?.subscription?.expiration
            ? dayjs(organization.subscription.expiration)
            : null;

        if (!organization?.subscription || subscriptionExpiration?.isBefore(dayjs())) {
            // TODO: FEATURE FLAG - When Stripe is disabled (no publishable key), give everyone paid plan with all features
            // Otherwise use free plan. Remove this conditional when re-enabling payments.
            const targetPlan = !stripePromise
                ? plans.find((plan) => plan.monetaryType === MonetaryType.Paid)
                : plans.find((plan) => plan.monetaryType === MonetaryType.Free);
            // END TODO: FEATURE FLAG - Change to: const targetPlan = plans.find((plan) => plan.monetaryType === MonetaryType.Free);

            if (targetPlan?.id) {
                await handleSubscriptionCreate(targetPlan.id);
            } else {
                logger.error("No target plan found or plan has no ID");
            }
        }
        setRedirectToHome(true);
    };

    if (redirectToHome) {
        setSelectSubscription(SubscriptionState.HIDDEN);
        return <Navigate to="/"/>;
    }

    const appearance: { theme: 'night'; variables: Record<string, string> } = {
        theme: 'night', // Use a literal type here
        variables: {
            fontFamily: 'Arial, sans-serif',
            fontSizeBase: '16px',
            borderRadius: '12px',
            colorBackground: '#ffffff',
            colorPrimary: '#000000',
            colorDanger: 'red',
            spacingUnit: '4px',
        },
    };

    const elementsOptions: StripeElementsOptions = { appearance };

    if (cardFormVisible) {
        // TODO: FEATURE FLAG - If Stripe is not initialized (payments disabled), give paid plan with all features
        // Remove this entire if block when re-enabling payments
        if (!stripePromise || !StripeElements) {
            logger.log('Card form requested but Stripe not initialized - giving paid plan with all features');
            const paidPlan = plans.find((plan) => plan.monetaryType === MonetaryType.Paid);
            if (paidPlan?.id) {
                handleSubscriptionCreate(paidPlan.id);
            }
            setCardFormVisible(false);
            setRedirectToHome(true);
            return null;
        }
        // END TODO: FEATURE FLAG

        return <StripeElements stripe={stripePromise} options={elementsOptions}>
            <PaymentDetails createSubscriptionCallback={handleSubscriptionCreate} selectedPlanId={selectedPlan}
                            plans={plans}/>
        </StripeElements>;
    }

    return (
        <Card
            bordered={false}
        >
            <div className="plans-container">
                <img
                    alt="logo"
                    src={require('../assets/logo.png')}
                    width={isMobileOnly ? 300 : 420}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setRedirectToHome(true)}
                />
                <div className="plans-card">
                    {showError ? <Alert message={<FormattedMessage id={"choose_plan_msg"}/>}
                                        type="error"
                                        className="mb-24"
                                        closeIcon={<CloseOutlined/>}
                                        onClose={handleAlertClick}
                                        showIcon
                                        onClick={handleAlertClick}
                    /> : null}
                    {plans.length > 0 && (
                        <div className="plans-card-content">

                            <Space direction="vertical">
                        <span className="font-family-roboto font-weight-500 font-20"><FormattedMessage
                            id="choose_plan"/></span>
                                <span
                                    className="font-family-roboto font-weight-400 font-14 no-wrap-text">
                                     <FormattedMessage id="compare_plans"/>{' '}
                                    <a
                                        href={intl.locale === 'BG' ? 'https://dentobook.com/bg/' : 'https://dentobook.com/en/'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                     <FormattedMessage id="here"/>
                                     </a>
                                    </span>
                                <Radio.Group onChange={() => {
                                }}>
                                    <div
                                        className={`plans-choice-container mt-32 ${
                                            organization?.isFreeTrialUsed ? "disabled-plan" : ""
                                        }`}
                                        key={plans.find((plan) => plan.monetaryType === MonetaryType.FreeTrial)?.id}
                                    >
                                        <Space>
                                            <Radio
                                                onChange={(e) => setSelectedPlan(e.target.value)}
                                                value={plans.find((plan) => plan.monetaryType === MonetaryType.FreeTrial)?.id}
                                                disabled={organization?.isFreeTrialUsed ?? undefined}
                                            />
                                            <Space direction="vertical">
                             <span
                                 className={`font-family-roboto font-weight-500 font-16 ${
                                     organization?.isFreeTrialUsed ? "text-disabled" : ""
                                 }`}
                             >
                         <FormattedMessage id="free_trial_title"/>
                        </span>
                                                <span
                                                    className={`font-family-roboto font-weight-400 font-14 ${
                                                        organization?.isFreeTrialUsed ? "text-disabled" : ""
                                                    }`}
                                                >
                <FormattedMessage id="subscription_description_free_trial"/>
            </span>
                                            </Space>
                                        </Space>
                                    </div>

                                    <div className="plans-choice-container mt-32"
                                         key={plans.find((plan) => plan.monetaryType === MonetaryType.Paid)?.id}>
                                        <Space>
                                            <Radio onChange={e => setSelectedPlan(e.target.value)}
                                                   value={plans.find((plan) => plan.monetaryType === MonetaryType.Paid)?.id}/>
                                            <Space direction="vertical">
                                        <span
                                            className="font-family-roboto font-weight-500 font-16"><FormattedMessage
                                            id="dentobook_pro_title"/></span>
                                                <span
                                                    className="font-family-roboto font-weight-400 font-14"><FormattedMessage
                                                    id="subscription_description"/></span>
                                            </Space>
                                        </Space>
                                    </div>
                                </Radio.Group>
                            </Space>
                        </div>
                    )}
                    <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                        <Button
                            className={"dentobook-btn-primary continue-btn mt-32"}
                            type="primary"
                            onClick={handleContinueBtnClick}
                        >
                            <FormattedMessage id="continue"/>
                        </Button>
                        <Button
                            className={"dentobook-btn-secondary continue-free-plan-btn mt-32"}
                            type="primary"
                            onClick={handleClose}
                        >
                            <FormattedMessage id="continue_free_version"/>
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    )
};

export default ChoosePlan;